import type { SVGProps } from 'react';
import { cn } from '@/lib/cn';
import {
  sportIcons,
  uiIcons,
  type IconDefinition,
  type IconName,
  type SportIconName,
} from './icons.generated';

export type { IconName, SportIconName };

interface BaseIconProps extends Omit<SVGProps<SVGSVGElement>, 'children' | 'viewBox'> {
  /** Rendered size in pixels; icons are square in the design system. */
  size?: number;
  /**
   * Accessible name. Omit for icons that only decorate adjacent text — the icon
   * is then hidden from assistive technology.
   */
  label?: string;
}

export interface IconProps extends BaseIconProps {
  name: IconName;
}

export interface SportIconProps extends BaseIconProps {
  name: SportIconName;
}

function renderIcon(
  definition: IconDefinition,
  { size = 20, label, className, ...rest }: BaseIconProps,
) {
  return (
    <svg
      viewBox={definition.viewBox}
      width={size}
      height={size}
      // Monochrome artwork is normalised to currentColor at build time, so colour
      // comes from the surrounding text colour.
      className={cn('inline-block shrink-0', className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      {...definition.attrs}
      {...rest}
      // Markup is generated from the committed design-system SVGs at build time,
      // never from user input.
      dangerouslySetInnerHTML={{ __html: definition.body }}
    />
  );
}

export function Icon({ name, ...props }: IconProps) {
  return renderIcon(uiIcons[name], props);
}

export function SportIcon({ name, ...props }: SportIconProps) {
  return renderIcon(sportIcons[name], props);
}
